import React, { useState, useEffect } from "react";
import {
  Card,
  Steps,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Button,
  Space,
  Typography,
  Tag,
  Row,
  Col,
  DatePicker,
  Divider,
  Alert,
  Slider,
  Checkbox,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckOutlined,
  BulbOutlined,
  FileTextOutlined,
  SearchOutlined,
  ScheduleOutlined,
  TagsOutlined,
  RocketOutlined,
  ReloadOutlined,
  LoadingOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import moment from "moment-timezone";

import { createBlogCampaign, generateBlogTitles } from "../../../../api/blog/blogCampaign";
import { getAllBlogCategories, getAllBlogTags } from "../../../../api/blog/blog";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Step } = Steps;

// Campaign type descriptions
const campaignTypeInfo = {
  topicBased: {
    title: "Topic Based",
    description: "Generate blogs around a main topic with related subtopics",
    icon: <BulbOutlined />,
    color: "#1890ff",
  },
  keywordBased: {
    title: "Keyword Based",
    description: "Target specific SEO keywords for better search rankings",
    icon: <SearchOutlined />,
    color: "#52c41a",
  },
  seriesBased: {
    title: "Series Based",
    description: "Create interconnected blog series with consistent themes",
    icon: <FileTextOutlined />,
    color: "#722ed1",
  },
};

// Tone options
const toneOptions = [
  { value: "professional", label: "Professional", description: "Formal, authoritative tone" },
  { value: "conversational", label: "Conversational", description: "Friendly, approachable tone" },
  { value: "technical", label: "Technical", description: "Detailed, expert-level content" },
  { value: "friendly", label: "Friendly", description: "Warm, engaging tone" },
];

// Search intent options
const searchIntentOptions = [
  { value: "informational", label: "Informational", description: "Answer questions, provide info" },
  { value: "transactional", label: "Transactional", description: "Drive conversions & actions" },
  { value: "navigational", label: "Navigational", description: "Help users find specific pages" },
  { value: "commercial", label: "Commercial", description: "Compare products & services" },
];

// Schema type options
const schemaTypeOptions = [
  { value: "Article", label: "Article Schema" },
  { value: "FAQPage", label: "FAQ Schema" },
  { value: "HowTo", label: "How-To Schema" },
  { value: "BreadcrumbList", label: "Breadcrumb Schema" },
];

// AI Provider options
const aiProviderOptions = [
  { value: "openai", label: "OpenAI (GPT-4)" },
  { value: "anthropic", label: "Anthropic (Claude)" },
  { value: "deepseek", label: "DeepSeek" },
];

// Cover image style options
const coverImageStyleOptions = [
  { value: "template", label: "Branded Template", description: "Copilotly template with title overlay" },
  { value: "gradient", label: "Auto Gradient", description: "Generate gradient cover" },
  { value: "placeholder", label: "Placeholder", description: "Use placeholder image" },
  { value: "none", label: "None", description: "No cover image" },
];

// Publishing strategy options
const publishingStrategyOptions = [
  { value: "immediate", label: "Immediate", description: "All blogs published with today's date" },
  { value: "drip", label: "Drip (Forward)", description: "Spread publish dates forward from today" },
  { value: "backdate", label: "Backdate (Recommended)", description: "Spread publish dates backward for natural SEO" },
];

// Industry options (Copilotly-focused)
const industryOptions = [
  { value: "saas", label: "SaaS & Software" },
  { value: "ecommerce", label: "E-commerce & Retail" },
  { value: "healthcare", label: "Healthcare & Medical" },
  { value: "real-estate", label: "Real Estate" },
  { value: "finance", label: "Finance & Banking" },
  { value: "education", label: "Education & E-learning" },
  { value: "hospitality", label: "Hospitality & Travel" },
  { value: "automotive", label: "Automotive" },
  { value: "legal", label: "Legal Services" },
  { value: "insurance", label: "Insurance" },
  { value: "hr", label: "HR & Recruitment" },
  { value: "marketing", label: "Marketing & Agencies" },
  { value: "general", label: "General Business" },
];

// Topic suggestions based on Copilotly's domain (AI writing assistant & browser extension)
const topicSuggestions = [
  // Core AI Writing Assistant Features
  {
    topic: "AI Writing Assistants for Content Creation",
    subtopics: ["Blog Writing", "Email Composition", "Social Media Posts", "Long-form Content"],
    keywords: ["ai writing assistant", "ai content writer", "automated writing tools"],
  },
  {
    topic: "Browser Extension Productivity Tools",
    subtopics: ["Chrome Extensions", "Edge Add-ons", "Firefox Extensions", "Cross-browser Support"],
    keywords: ["browser extension", "productivity extension", "chrome extension tools"],
  },
  {
    topic: "AI-Powered Email Writing",
    subtopics: ["Email Templates", "Tone Adjustment", "Professional Emails", "Response Generation"],
    keywords: ["ai email writer", "email assistant", "automated email composition"],
  },
  {
    topic: "Content Rewriting and Paraphrasing",
    subtopics: ["Text Rewriter", "Paraphrasing Tools", "Content Spinning", "Plagiarism-free Writing"],
    keywords: ["ai rewriter", "paraphrasing tool", "content rewriting"],
  },
  {
    topic: "Grammar and Spell Checking with AI",
    subtopics: ["Grammar Correction", "Spell Check", "Style Suggestions", "Writing Enhancement"],
    keywords: ["ai grammar checker", "spell check tool", "writing assistant"],
  },
  {
    topic: "AI Content Summarization",
    subtopics: ["Text Summarizer", "Article Summary", "Document Condensing", "Key Points Extraction"],
    keywords: ["ai summarizer", "text summary tool", "content condensing"],
  },
  {
    topic: "AI for Social Media Content",
    subtopics: ["Post Generation", "Caption Writing", "Hashtag Suggestions", "Engagement Optimization"],
    keywords: ["social media ai", "ai caption writer", "social content generator"],
  },
  {
    topic: "Professional Writing Enhancement",
    subtopics: ["Business Writing", "Formal Tone", "Report Writing", "Presentation Content"],
    keywords: ["professional writing assistant", "business writing ai", "formal writing tool"],
  },
  {
    topic: "Creative Writing with AI",
    subtopics: ["Story Generation", "Creative Ideas", "Fiction Writing", "Narrative Development"],
    keywords: ["ai creative writing", "story generator", "creative writing assistant"],
  },
  {
    topic: "SEO Content Optimization",
    subtopics: ["Keyword Integration", "Meta Descriptions", "SEO-friendly Content", "Search Rankings"],
    keywords: ["seo writing tool", "ai seo optimizer", "seo content assistant"],
  },
  // Browser Extension Features
  {
    topic: "Context-Aware Writing Assistance",
    subtopics: ["Real-time Suggestions", "Platform Integration", "Inline Editing", "Contextual Help"],
    keywords: ["context-aware ai", "real-time writing help", "inline writing assistant"],
  },
  {
    topic: "Multi-language Writing Support",
    subtopics: ["Translation", "Multilingual Content", "Language Detection", "Cross-language Writing"],
    keywords: ["multilingual ai writer", "translation assistant", "language support tool"],
  },
  {
    topic: "AI-Powered Note Taking",
    subtopics: ["Quick Notes", "Meeting Notes", "Research Organization", "Note Enhancement"],
    keywords: ["ai note taking", "smart notes", "note assistant"],
  },
  {
    topic: "Document Collaboration Tools",
    subtopics: ["Team Writing", "Shared Documents", "Version Control", "Collaborative Editing"],
    keywords: ["collaborative writing tool", "team writing assistant", "document collaboration"],
  },
  {
    topic: "AI Writing for Students",
    subtopics: ["Essay Writing", "Research Papers", "Study Notes", "Academic Writing"],
    keywords: ["student writing assistant", "essay helper", "academic writing tool"],
  },
  // Productivity & Workflow
  {
    topic: "Writing Productivity Hacks",
    subtopics: ["Time-saving Tools", "Workflow Automation", "Quick Responses", "Template Management"],
    keywords: ["writing productivity", "writing workflow", "productivity hacks"],
  },
  {
    topic: "AI Templates for Common Tasks",
    subtopics: ["Email Templates", "Report Templates", "Proposal Templates", "Message Templates"],
    keywords: ["ai templates", "writing templates", "template automation"],
  },
  {
    topic: "Personal Writing Assistant",
    subtopics: ["Custom Preferences", "Writing Style", "Personalized Suggestions", "User Learning"],
    keywords: ["personal ai assistant", "custom writing tool", "personalized ai"],
  },
  {
    topic: "Browser-based Writing Tools",
    subtopics: ["Web Writing", "Form Filling", "Comment Responses", "Online Editing"],
    keywords: ["browser writing tool", "web-based editor", "online writing assistant"],
  },
  {
    topic: "AI for Marketing Copy",
    subtopics: ["Ad Copy", "Landing Pages", "Product Descriptions", "Marketing Content"],
    keywords: ["ai copywriting", "marketing copy generator", "advertising content ai"],
  },
  // Industry-Specific Use Cases
  {
    topic: "AI Writing for E-commerce",
    subtopics: ["Product Descriptions", "Category Pages", "Customer Reviews", "Store Content"],
    keywords: ["ecommerce writing ai", "product description generator", "store content tool"],
  },
  {
    topic: "Legal Writing Assistance",
    subtopics: ["Contract Writing", "Legal Documents", "Compliance Content", "Terms & Conditions"],
    keywords: ["legal writing ai", "contract assistant", "legal document tool"],
  },
  {
    topic: "Healthcare Content Writing",
    subtopics: ["Medical Writing", "Patient Education", "Health Articles", "Clinical Documentation"],
    keywords: ["medical writing ai", "healthcare content", "clinical writing assistant"],
  },
  {
    topic: "Technical Writing with AI",
    subtopics: ["Documentation", "API Docs", "User Manuals", "Technical Guides"],
    keywords: ["technical writing ai", "documentation assistant", "technical content tool"],
  },
  {
    topic: "Real Estate Content Creation",
    subtopics: ["Property Listings", "Market Reports", "Agent Bios", "Neighborhood Guides"],
    keywords: ["real estate writing ai", "listing description tool", "property content generator"],
  },
  {
    topic: "Financial Writing Tools",
    subtopics: ["Financial Reports", "Investment Content", "Market Analysis", "Business Writing"],
    keywords: ["financial writing ai", "business content tool", "finance writing assistant"],
  },
  {
    topic: "HR and Recruitment Content",
    subtopics: ["Job Descriptions", "Employee Communications", "Recruitment Messages", "HR Documents"],
    keywords: ["hr writing ai", "job description generator", "recruitment content tool"],
  },
  {
    topic: "Journalism and News Writing",
    subtopics: ["Article Writing", "News Content", "Press Releases", "Reporting Tools"],
    keywords: ["journalism ai", "news writing tool", "article generator"],
  },
  // Advanced Features
  {
    topic: "AI Tone and Style Adjustment",
    subtopics: ["Formal vs Casual", "Professional Tone", "Friendly Voice", "Brand Voice"],
    keywords: ["tone adjustment ai", "style modifier", "voice customization"],
  },
  {
    topic: "Content Expansion and Elaboration",
    subtopics: ["Text Expansion", "Detail Addition", "Content Development", "Idea Elaboration"],
    keywords: ["content expander", "text elaboration tool", "ai content development"],
  },
  {
    topic: "Writing Quality Metrics",
    subtopics: ["Readability Scores", "Quality Analysis", "Engagement Metrics", "Content Scoring"],
    keywords: ["writing quality tool", "readability checker", "content metrics"],
  },
  {
    topic: "AI-Powered Proofreading",
    subtopics: ["Error Detection", "Consistency Check", "Style Compliance", "Final Review"],
    keywords: ["ai proofreading", "automated editing", "proofreader assistant"],
  },
  {
    topic: "Voice-to-Text with AI Enhancement",
    subtopics: ["Dictation Tools", "Speech-to-Text", "Voice Writing", "Audio Transcription"],
    keywords: ["voice to text ai", "dictation assistant", "speech writing tool"],
  },
  {
    topic: "Content Personalization",
    subtopics: ["Audience Targeting", "Personalized Messages", "Custom Content", "User Segmentation"],
    keywords: ["content personalization", "personalized writing", "targeted content ai"],
  },
  {
    topic: "Writing Analytics and Insights",
    subtopics: ["Usage Analytics", "Writing Patterns", "Improvement Tracking", "Performance Metrics"],
    keywords: ["writing analytics", "content insights", "writing performance"],
  },
  // Comparison & Strategy
  {
    topic: "AI Writing Tools Comparison",
    subtopics: ["Feature Comparison", "Pricing Analysis", "Best Tools", "Tool Selection"],
    keywords: ["ai writing tools comparison", "best ai writer", "writing tool review"],
  },
  {
    topic: "Chrome Extension Development",
    subtopics: ["Extension Architecture", "API Integration", "User Interface", "Performance Optimization"],
    keywords: ["chrome extension development", "browser extension guide", "extension building"],
  },
  {
    topic: "AI Writing Best Practices",
    subtopics: ["Effective Use", "Quality Output", "Workflow Integration", "Common Mistakes"],
    keywords: ["ai writing best practices", "writing ai tips", "effective ai use"],
  },
  {
    topic: "Privacy and Security in Writing Tools",
    subtopics: ["Data Protection", "Privacy Compliance", "Secure Writing", "User Data Safety"],
    keywords: ["writing tool privacy", "secure ai writing", "data protection"],
  },
  {
    topic: "Future of AI Writing Technology",
    subtopics: ["AI Trends", "Technology Evolution", "Future Features", "Industry Predictions"],
    keywords: ["future of ai writing", "writing ai trends 2025", "next-gen writing tools"],
  },
  {
    topic: "Integrating AI into Writing Workflow",
    subtopics: ["Workflow Optimization", "Tool Integration", "Process Automation", "Efficiency Tips"],
    keywords: ["writing workflow ai", "ai integration", "workflow automation"],
  },
  {
    topic: "AI Writing for Bloggers",
    subtopics: ["Blog Post Creation", "Content Ideas", "Consistent Publishing", "Audience Engagement"],
    keywords: ["blogging ai", "blog writing assistant", "content creation tool"],
  },
  {
    topic: "Custom Writing Shortcuts",
    subtopics: ["Text Expansion", "Quick Phrases", "Keyboard Shortcuts", "Snippet Management"],
    keywords: ["writing shortcuts", "text expander", "quick text tool"],
  },
  {
    topic: "AI for Professional Communication",
    subtopics: ["Business Emails", "Client Messages", "Team Communication", "Professional Correspondence"],
    keywords: ["professional communication ai", "business writing tool", "corporate communication"],
  },
  {
    topic: "Content Strategy with AI",
    subtopics: ["Content Planning", "Topic Research", "Publishing Schedule", "Strategy Development"],
    keywords: ["content strategy ai", "content planning tool", "strategic writing"],
  },
];

// Category suggestions for blogs
const categorySuggestions = [
  "AI Writing Tools",
  "Browser Extensions",
  "Content Creation",
  "Productivity",
  "Writing Tips",
  "SEO Writing",
  "Professional Writing",
  "Product Updates",
  "Best Practices",
  "Case Studies",
  "Writing Strategy",
  "AI Technology",
];

// Format timezone for display
const formatTimezone = (tz) => {
  const offset = moment.tz(tz).format("Z");
  return `${tz} (GMT${offset})`;
};

const BlogCampaignCreate = () => {
  const history = useHistory();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);

  // Title generation state
  const [generatedTitles, setGeneratedTitles] = useState([]);
  const [titlesLoading, setTitlesLoading] = useState(false);
  const [titlesGenerated, setTitlesGenerated] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    // Basic
    name: "",
    description: "",
    campaignType: "topicBased",

    // Topic Config
    mainTopic: "",
    subtopics: [],
    targetKeywords: [],
    audience: "business owners and marketers",
    tone: "professional",
    industry: "",

    // Content Config
    wordCountMin: 1500,
    wordCountMax: 2500,
    includesFAQ: true,
    faqCount: 8,
    includesHowTo: true,
    includesCaseStudy: false,
    includesStatistics: true,
    internalLinkCount: 3,
    externalLinkCount: 2,

    // SEO Config
    generateSchema: true,
    schemaTypes: ["Article", "FAQPage"],
    optimizeForFeaturedSnippets: true,
    targetSearchIntent: "informational",

    // Generation Config
    blogCount: 5,
    minutesBetweenBlogs: 5,
    autoApprove: false,
    aiProvider: "openai",
    generateCoverImages: true,
    coverImageStyle: "template",
    publishingStrategy: "backdate",
    backdateStartDate: null,

    // Taxonomy Config
    category: null,
    autoCreateCategory: false,
    newCategoryName: "",
    selectedTags: [],
    autoCreateTags: true,
    suggestedTagCount: 5,

    // Schedule
    scheduleType: "immediate",
    scheduledFor: null,
    scheduledEndDate: null,
    timezone: moment.tz.guess(),
  });

  // Load categories and tags
  useEffect(() => {
    const loadData = async () => {
      try {
        const [catRes, tagRes] = await Promise.all([
          getAllBlogCategories(),
          getAllBlogTags(),
        ]);
        setCategories(catRes.data || []);
        setTags(tagRes.data || []);
      } catch (error) {
        console.error("Error loading categories/tags:", error);
      }
    };
    loadData();
  }, []);

  // Update form data
  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Validate current step
  const validateStep = () => {
    const errors = [];

    switch (currentStep) {
      case 0: // Campaign Type & Topic
        if (!formData.name.trim()) errors.push("Campaign name is required");
        if (!formData.campaignType) errors.push("Campaign type is required");
        if (!formData.mainTopic.trim()) errors.push("Main topic is required");
        if (formData.campaignType === "keywordBased" && formData.targetKeywords.length === 0) {
          errors.push("At least one target keyword is required for keyword-based campaigns");
        }
        break;

      case 1: // Content & SEO Settings
        if (formData.wordCountMin >= formData.wordCountMax) {
          errors.push("Minimum word count must be less than maximum");
        }
        if (formData.includesFAQ && (formData.faqCount < 3 || formData.faqCount > 15)) {
          errors.push("FAQ count must be between 3 and 15");
        }
        break;

      case 2: // Generation & Taxonomy
        if (!formData.category && !formData.autoCreateCategory) {
          errors.push("Please select a category or enable auto-create");
        }
        if (formData.autoCreateCategory && !formData.newCategoryName.trim()) {
          errors.push("New category name is required when auto-create is enabled");
        }
        break;

      case 3: // Title Selection
        const selectedCount = generatedTitles.filter((t) => t.selected).length;
        if (selectedCount === 0) {
          errors.push("Please select at least one title to generate content for");
        }
        break;

      case 4: // Schedule & Review
        if (formData.scheduleType === "scheduled" && !formData.scheduledFor) {
          errors.push("Please select a start date and time");
        }
        break;

      default:
        break;
    }

    if (errors.length > 0) {
      errors.forEach((err) => message.error(err));
      return false;
    }
    return true;
  };

  // Generate titles using AI
  const handleGenerateTitles = async () => {
    setTitlesLoading(true);
    try {
      const response = await generateBlogTitles({
        topicConfig: {
          mainTopic: formData.mainTopic,
          subtopics: formData.subtopics,
          targetKeywords: formData.targetKeywords,
          audience: formData.audience,
          tone: formData.tone,
          industry: formData.industry,
        },
        contentConfig: {
          targetSearchIntent: formData.targetSearchIntent,
        },
        count: 10,
      });

      // Axios returns data in response.data
      const data = response.data || response;

      if (data.success && data.titles) {
        // Add selected property to each title
        const titlesWithSelection = data.titles.map((t) => ({
          ...t,
          selected: true, // Default all selected
        }));
        setGeneratedTitles(titlesWithSelection);
        setTitlesGenerated(true);
        message.success(`Generated ${titlesWithSelection.length} title suggestions!`);
      } else {
        throw new Error(data.message || "Failed to generate titles");
      }
    } catch (error) {
      console.error("Error generating titles:", error);
      message.error(error.message || "Failed to generate titles. Please try again.");
    } finally {
      setTitlesLoading(false);
    }
  };

  // Toggle title selection
  const toggleTitleSelection = (index) => {
    setGeneratedTitles((prev) =>
      prev.map((t, i) => (i === index ? { ...t, selected: !t.selected } : t))
    );
  };

  // Select all titles
  const selectAllTitles = () => {
    setGeneratedTitles((prev) => prev.map((t) => ({ ...t, selected: true })));
  };

  // Deselect all titles
  const deselectAllTitles = () => {
    setGeneratedTitles((prev) => prev.map((t) => ({ ...t, selected: false })));
  };

  // Handle next step
  const handleNext = async () => {
    if (!validateStep()) return;

    // Auto-generate titles when entering step 3 (title selection)
    if (currentStep === 2 && !titlesGenerated) {
      await handleGenerateTitles();
    }

    setCurrentStep((prev) => prev + 1);
  };

  // Handle previous step
  const handlePrev = () => {
    setCurrentStep((prev) => prev - 1);
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateStep()) return;

    // Get selected titles
    const selectedTitles = generatedTitles.filter((t) => t.selected);
    if (selectedTitles.length === 0) {
      message.error("Please select at least one title");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        campaignType: formData.campaignType,

        topicConfig: {
          mainTopic: formData.mainTopic.trim(),
          subtopics: formData.subtopics,
          targetKeywords: formData.targetKeywords,
          audience: formData.audience.trim(),
          tone: formData.tone,
          industry: formData.industry.trim(),
        },

        contentConfig: {
          wordCountMin: formData.wordCountMin,
          wordCountMax: formData.wordCountMax,
          includesFAQ: formData.includesFAQ,
          faqCount: formData.faqCount,
          includesHowTo: formData.includesHowTo,
          includesCaseStudy: formData.includesCaseStudy,
          includesStatistics: formData.includesStatistics,
          internalLinkCount: formData.internalLinkCount,
          externalLinkCount: formData.externalLinkCount,
        },

        seoConfig: {
          generateSchema: formData.generateSchema,
          schemaTypes: formData.schemaTypes,
          optimizeForFeaturedSnippets: formData.optimizeForFeaturedSnippets,
          targetSearchIntent: formData.targetSearchIntent,
        },

        generationConfig: {
          blogCount: selectedTitles.length, // Use selected count
          minutesBetweenBlogs: formData.minutesBetweenBlogs,
          autoApprove: formData.autoApprove,
          aiProvider: formData.aiProvider,
          generateCoverImages: formData.generateCoverImages,
          coverImageStyle: formData.coverImageStyle,
          publishingStrategy: formData.publishingStrategy,
          backdateStartDate: formData.backdateStartDate,
        },

        taxonomyConfig: {
          category: formData.category,
          autoCreateCategory: formData.autoCreateCategory,
          newCategoryName: formData.newCategoryName.trim(),
          tags: formData.selectedTags,
          autoCreateTags: formData.autoCreateTags,
          suggestedTagCount: formData.suggestedTagCount,
        },

        // Include selected blog titles
        blogTitles: selectedTitles.map((t) => ({
          title: t.title,
          targetKeyword: t.targetKeyword,
          searchIntent: t.searchIntent,
        })),

        scheduleType: formData.scheduleType,
        scheduledFor: formData.scheduleType === "scheduled" ? formData.scheduledFor : null,
        scheduledEndDate: formData.scheduledEndDate,
        timezone: formData.timezone,
      };

      const response = await createBlogCampaign(payload);
      const data = response.data || response;
      message.success("Blog campaign created successfully!");
      history.push(`/super-admin/blog-campaign/${data.campaign._id}/progress`);
    } catch (error) {
      console.error("Error creating campaign:", error);
      message.error(error.response?.data?.message || "Failed to create campaign");
    } finally {
      setLoading(false);
    }
  };

  // Handle topic suggestion selection
  const handleTopicSuggestionClick = (suggestion) => {
    setFormData((prev) => ({
      ...prev,
      mainTopic: suggestion.topic,
      subtopics: suggestion.subtopics,
      targetKeywords: suggestion.keywords,
      name: prev.name || `${suggestion.topic} Blog Series`,
    }));
  };

  // Render Step 1: Campaign Type & Topic
  const renderStep1 = () => (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>
        Campaign Type & Topic
      </Title>

      {/* Quick Start Topic Suggestions */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
        message="Quick Start: Click a topic to auto-fill"
        description={
          <div style={{ marginTop: 12 }}>
            <Input
              placeholder="Search topics..."
              size="small"
              style={{ marginBottom: 12, maxWidth: 300 }}
              onChange={(e) => {
                const searchEl = document.getElementById("topic-search-results");
                if (searchEl) {
                  const searchTerm = e.target.value.toLowerCase();
                  const tags = searchEl.querySelectorAll("[data-topic]");
                  tags.forEach((tag) => {
                    const topic = tag.getAttribute("data-topic").toLowerCase();
                    tag.style.display = topic.includes(searchTerm) ? "inline-block" : "none";
                  });
                }
              }}
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            />
            <div
              id="topic-search-results"
              style={{
                maxHeight: 200,
                overflowY: "auto",
                paddingRight: 8,
              }}
            >
              {topicSuggestions.map((suggestion, idx) => (
                <Tag
                  key={idx}
                  data-topic={suggestion.topic}
                  color={formData.mainTopic === suggestion.topic ? "blue" : "default"}
                  style={{
                    cursor: "pointer",
                    padding: "4px 12px",
                    fontSize: 13,
                    marginBottom: 8,
                  }}
                  onClick={() => handleTopicSuggestionClick(suggestion)}
                >
                  {suggestion.topic}
                </Tag>
              ))}
            </div>
          </div>
        }
      />

      {/* Campaign Name */}
      <Form.Item label="Campaign Name" required>
        <Input
          size="large"
          placeholder="e.g., AI Chatbot Best Practices Series"
          value={formData.name}
          onChange={(e) => updateFormData("name", e.target.value)}
          maxLength={100}
        />
      </Form.Item>

      {/* Description */}
      <Form.Item label="Description (optional)">
        <TextArea
          rows={2}
          placeholder="Brief description of this campaign"
          value={formData.description}
          onChange={(e) => updateFormData("description", e.target.value)}
          maxLength={500}
        />
      </Form.Item>

      {/* Campaign Type */}
      <Form.Item label="Campaign Type" required>
        <Row gutter={[16, 16]}>
          {Object.entries(campaignTypeInfo).map(([key, info]) => (
            <Col xs={24} sm={8} key={key}>
              <Card
                hoverable
                style={{
                  borderColor: formData.campaignType === key ? info.color : "#d9d9d9",
                  borderWidth: formData.campaignType === key ? 2 : 1,
                  cursor: "pointer",
                }}
                onClick={() => updateFormData("campaignType", key)}
              >
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: 28,
                      color: info.color,
                      marginBottom: 8,
                    }}
                  >
                    {info.icon}
                  </div>
                  <Title level={5} style={{ margin: 0 }}>
                    {info.title}
                  </Title>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {info.description}
                  </Text>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </Form.Item>

      <Divider />

      {/* Main Topic */}
      <Form.Item label="Main Topic" required>
        <Input
          size="large"
          placeholder="e.g., AI Chatbots for Business"
          value={formData.mainTopic}
          onChange={(e) => updateFormData("mainTopic", e.target.value)}
        />
      </Form.Item>

      {/* Subtopics with suggestions */}
      <Form.Item label="Subtopics">
        <Select
          mode="tags"
          size="large"
          placeholder="Select or type subtopics"
          value={formData.subtopics}
          onChange={(value) => updateFormData("subtopics", value)}
          style={{ width: "100%" }}
        >
          {/* Show related subtopics based on selected topic */}
          {topicSuggestions
            .find((s) => s.topic === formData.mainTopic)
            ?.subtopics.map((subtopic) => (
              <Option key={subtopic} value={subtopic}>
                {subtopic}
              </Option>
            ))}
        </Select>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Related subtopics help focus your content
        </Text>
      </Form.Item>

      {/* Target Keywords with suggestions */}
      <Form.Item
        label="Target Keywords"
        required={formData.campaignType === "keywordBased"}
      >
        <Select
          mode="tags"
          size="large"
          placeholder="Select or type SEO keywords"
          value={formData.targetKeywords}
          onChange={(value) => updateFormData("targetKeywords", value)}
          style={{ width: "100%" }}
        >
          {/* Show related keywords based on selected topic */}
          {topicSuggestions
            .find((s) => s.topic === formData.mainTopic)
            ?.keywords.map((keyword) => (
              <Option key={keyword} value={keyword}>
                {keyword}
              </Option>
            ))}
        </Select>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Primary keywords to target for SEO
        </Text>
      </Form.Item>

      {/* Target Audience */}
      <Form.Item label="Target Audience">
        <Input
          placeholder="e.g., Small business owners, Marketing managers"
          value={formData.audience}
          onChange={(e) => updateFormData("audience", e.target.value)}
        />
      </Form.Item>

      <Row gutter={16}>
        {/* Tone */}
        <Col xs={24} sm={12}>
          <Form.Item label="Content Tone">
            <Select
              size="large"
              value={formData.tone}
              onChange={(value) => updateFormData("tone", value)}
            >
              {toneOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label} - {opt.description}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        {/* Industry - Now a dropdown */}
        <Col xs={24} sm={12}>
          <Form.Item label="Industry">
            <Select
              size="large"
              placeholder="Select industry"
              value={formData.industry}
              onChange={(value) => updateFormData("industry", value)}
              allowClear
            >
              {industryOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  // Render Step 2: Content & SEO Settings
  const renderStep2 = () => (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>
        Content & SEO Settings
      </Title>

      {/* Word Count Range */}
      <Form.Item label="Word Count Range">
        <Row gutter={16} align="middle">
          <Col span={8}>
            <InputNumber
              min={500}
              max={5000}
              value={formData.wordCountMin}
              onChange={(value) => updateFormData("wordCountMin", value)}
              addonBefore="Min"
              style={{ width: "100%" }}
            />
          </Col>
          <Col span={8}>
            <InputNumber
              min={1000}
              max={8000}
              value={formData.wordCountMax}
              onChange={(value) => updateFormData("wordCountMax", value)}
              addonBefore="Max"
              style={{ width: "100%" }}
            />
          </Col>
          <Col span={8}>
            <Text type="secondary">
              Average: {Math.round((formData.wordCountMin + formData.wordCountMax) / 2)} words
            </Text>
          </Col>
        </Row>
      </Form.Item>

      <Divider>Content Sections</Divider>

      {/* FAQ Section */}
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Switch
                checked={formData.includesFAQ}
                onChange={(checked) => updateFormData("includesFAQ", checked)}
              />
              <span>Include FAQ Section</span>
            </div>
            {formData.includesFAQ && (
              <div style={{ marginTop: 12 }}>
                <Text type="secondary">Number of FAQ questions:</Text>
                <Slider
                  min={3}
                  max={15}
                  value={formData.faqCount}
                  onChange={(value) => updateFormData("faqCount", value)}
                  marks={{ 3: "3", 8: "8", 15: "15" }}
                />
              </div>
            )}
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Switch
                checked={formData.includesHowTo}
                onChange={(checked) => updateFormData("includesHowTo", checked)}
              />
              <span>Include How-To Steps</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Switch
                checked={formData.includesCaseStudy}
                onChange={(checked) => updateFormData("includesCaseStudy", checked)}
              />
              <span>Include Case Study</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Switch
                checked={formData.includesStatistics}
                onChange={(checked) => updateFormData("includesStatistics", checked)}
              />
              <span>Include Statistics & Data</span>
            </div>
          </Space>
        </Col>
      </Row>

      {/* Links */}
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item label="Internal Links per Blog">
            <Slider
              min={0}
              max={10}
              value={formData.internalLinkCount}
              onChange={(value) => updateFormData("internalLinkCount", value)}
              marks={{ 0: "0", 5: "5", 10: "10" }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label="External Links per Blog">
            <Slider
              min={0}
              max={5}
              value={formData.externalLinkCount}
              onChange={(value) => updateFormData("externalLinkCount", value)}
              marks={{ 0: "0", 2: "2", 5: "5" }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider>SEO Configuration</Divider>

      {/* Search Intent */}
      <Form.Item label="Target Search Intent">
        <Select
          size="large"
          value={formData.targetSearchIntent}
          onChange={(value) => updateFormData("targetSearchIntent", value)}
        >
          {searchIntentOptions.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label} - {opt.description}
            </Option>
          ))}
        </Select>
      </Form.Item>

      {/* Schema Markup */}
      <Form.Item>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <Switch
            checked={formData.generateSchema}
            onChange={(checked) => updateFormData("generateSchema", checked)}
          />
          <span>Generate Schema Markup (JSON-LD)</span>
        </div>
        {formData.generateSchema && (
          <Checkbox.Group
            options={schemaTypeOptions.map((opt) => ({
              label: opt.label,
              value: opt.value,
            }))}
            value={formData.schemaTypes}
            onChange={(values) => updateFormData("schemaTypes", values)}
          />
        )}
      </Form.Item>

      {/* Featured Snippets */}
      <Form.Item>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Switch
            checked={formData.optimizeForFeaturedSnippets}
            onChange={(checked) => updateFormData("optimizeForFeaturedSnippets", checked)}
          />
          <span>Optimize for Featured Snippets</span>
        </div>
        <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
          Structure content for Google's featured snippet boxes
        </Text>
      </Form.Item>
    </div>
  );

  // Render Step 3: Generation & Taxonomy
  const renderStep3 = () => (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>
        Generation & Taxonomy Settings
      </Title>

      <Alert
        type="info"
        message="Blog count determined by title selection"
        description="In the next step, AI will generate title suggestions and you'll select which ones to create content for."
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* Minutes Between Blogs */}
      <Form.Item label="Minutes Between Blog Generations">
        <Row gutter={16} align="middle">
          <Col span={16}>
            <Slider
              min={1}
              max={60}
              value={formData.minutesBetweenBlogs}
              onChange={(value) => updateFormData("minutesBetweenBlogs", value)}
              marks={{ 1: "1", 15: "15", 30: "30", 60: "60" }}
            />
          </Col>
          <Col span={8}>
            <Text type="secondary">
              {formData.minutesBetweenBlogs} min per blog
            </Text>
          </Col>
        </Row>
      </Form.Item>

      <Row gutter={16}>
        {/* AI Provider */}
        <Col xs={24} sm={12}>
          <Form.Item label="AI Provider">
            <Select
              size="large"
              value={formData.aiProvider}
              onChange={(value) => updateFormData("aiProvider", value)}
            >
              {aiProviderOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        {/* Cover Image Style */}
        <Col xs={24} sm={12}>
          <Form.Item label="Cover Image">
            <Select
              size="large"
              value={formData.coverImageStyle}
              onChange={(value) => {
                updateFormData("coverImageStyle", value);
                updateFormData("generateCoverImages", value !== "none");
              }}
            >
              {coverImageStyleOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label} - {opt.description}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      {/* Auto Approve */}
      <Form.Item>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Switch
            checked={formData.autoApprove}
            onChange={(checked) => updateFormData("autoApprove", checked)}
          />
          <span>Auto-approve generated blogs</span>
        </div>
        <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
          If disabled, blogs will be saved as drafts for review
        </Text>
      </Form.Item>

      {/* Publishing Strategy */}
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item label="Publishing Strategy">
            <Select
              size="large"
              value={formData.publishingStrategy}
              onChange={(value) => updateFormData("publishingStrategy", value)}
            >
              {publishingStrategyOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label} - {opt.description}
                </Option>
              ))}
            </Select>
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
              {formData.publishingStrategy === "backdate"
                ? "Blogs will appear as if published over time (best for SEO)"
                : formData.publishingStrategy === "drip"
                ? "Blogs will be scheduled to publish in the future"
                : "All blogs will have today's publish date"}
            </Text>
          </Form.Item>
        </Col>
        {formData.publishingStrategy === "backdate" && (
          <Col xs={24} sm={12}>
            <Form.Item label="Start Publishing From">
              <DatePicker
                size="large"
                style={{ width: "100%" }}
                value={formData.backdateStartDate ? moment(formData.backdateStartDate) : null}
                onChange={(date) => updateFormData("backdateStartDate", date?.toISOString())}
                disabledDate={(current) => current && current > moment().endOf("day")}
                placeholder="Select start date"
              />
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
                First blog publishes on this date, then each next blog {formData.minutesBetweenBlogs >= 1440 ? `${Math.floor(formData.minutesBetweenBlogs / 1440)} day(s)` : `${formData.minutesBetweenBlogs} min`} later
              </Text>
            </Form.Item>
          </Col>
        )}
      </Row>

      <Divider>Category & Tags</Divider>

      {/* Category Selection */}
      <Form.Item label="Category" required>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <Switch
            checked={formData.autoCreateCategory}
            onChange={(checked) => {
              updateFormData("autoCreateCategory", checked);
              if (checked) updateFormData("category", null);
            }}
          />
          <span>Create new category</span>
        </div>

        {formData.autoCreateCategory ? (
          <>
            <Input
              size="large"
              placeholder="New category name"
              value={formData.newCategoryName}
              onChange={(e) => updateFormData("newCategoryName", e.target.value)}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>
                Suggestions:
              </Text>
              {categorySuggestions.map((cat) => (
                <Tag
                  key={cat}
                  color={formData.newCategoryName === cat ? "blue" : "default"}
                  style={{ cursor: "pointer", marginBottom: 4 }}
                  onClick={() => updateFormData("newCategoryName", cat)}
                >
                  {cat}
                </Tag>
              ))}
            </div>
            {categories.length > 0 && (
              <div style={{ marginTop: 12, padding: 12, background: "#f5f5f5", borderRadius: 6 }}>
                <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
                  Existing categories (avoid duplicates):
                </Text>
                {categories.map((cat) => (
                  <Tag key={cat._id} color="orange" style={{ marginBottom: 4 }}>
                    {cat.name}
                  </Tag>
                ))}
              </div>
            )}
          </>
        ) : (
          <Select
            size="large"
            placeholder="Select a category"
            value={formData.category}
            onChange={(value) => updateFormData("category", value)}
            style={{ width: "100%" }}
            allowClear
          >
            {categories.map((cat) => (
              <Option key={cat._id} value={cat._id}>
                {cat.name}
              </Option>
            ))}
          </Select>
        )}
      </Form.Item>

      {/* Tags */}
      <Form.Item label="Tags">
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <Switch
            checked={formData.autoCreateTags}
            onChange={(checked) => updateFormData("autoCreateTags", checked)}
          />
          <span>Auto-create tags from content</span>
        </div>

        {formData.autoCreateTags && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">Suggested tags per blog:</Text>
            <Slider
              min={1}
              max={10}
              value={formData.suggestedTagCount}
              onChange={(value) => updateFormData("suggestedTagCount", value)}
              marks={{ 1: "1", 5: "5", 10: "10" }}
              style={{ maxWidth: 300 }}
            />
          </div>
        )}

        <Select
          mode="multiple"
          size="large"
          placeholder="Select existing tags (optional)"
          value={formData.selectedTags}
          onChange={(value) => updateFormData("selectedTags", value)}
          style={{ width: "100%" }}
        >
          {tags.map((tag) => (
            <Option key={tag._id} value={tag._id}>
              {tag.name}
            </Option>
          ))}
        </Select>
        <Text type="secondary" style={{ fontSize: 12 }}>
          These tags will be applied to all generated blogs
        </Text>
      </Form.Item>
    </div>
  );

  // Render Step 4: Title Selection
  const renderStep4 = () => {
    const selectedCount = generatedTitles.filter((t) => t.selected).length;

    return (
      <div>
        <Title level={4} style={{ marginBottom: 24 }}>
          Select Blog Titles
        </Title>

        <Alert
          type="info"
          message="AI has generated title suggestions based on your topic"
          description="Select the titles you want to generate blog content for. You can regenerate titles if you're not satisfied with the suggestions."
          showIcon
          style={{ marginBottom: 24 }}
        />

        {/* Title actions */}
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Space>
            <Button onClick={selectAllTitles}>Select All</Button>
            <Button onClick={deselectAllTitles}>Deselect All</Button>
            <Button
              icon={titlesLoading ? <LoadingOutlined /> : <ReloadOutlined />}
              onClick={handleGenerateTitles}
              loading={titlesLoading}
            >
              Regenerate Titles
            </Button>
          </Space>
          <Tag color={selectedCount > 0 ? "green" : "red"}>
            {selectedCount} title{selectedCount !== 1 ? "s" : ""} selected
          </Tag>
        </div>

        {/* Loading state */}
        {titlesLoading && (
          <div style={{ textAlign: "center", padding: 40 }}>
            <LoadingOutlined style={{ fontSize: 32, marginBottom: 16 }} />
            <div>
              <Text type="secondary">Generating title suggestions...</Text>
            </div>
          </div>
        )}

        {/* Generated titles */}
        {!titlesLoading && generatedTitles.length > 0 && (
          <div style={{ maxHeight: 500, overflowY: "auto" }}>
            {generatedTitles.map((title, index) => (
              <Card
                key={index}
                size="small"
                hoverable
                style={{
                  marginBottom: 12,
                  borderColor: title.selected ? "#52c41a" : "#d9d9d9",
                  borderWidth: title.selected ? 2 : 1,
                  background: title.selected ? "#f6ffed" : "#fff",
                  cursor: "pointer",
                }}
                onClick={() => toggleTitleSelection(index)}
              >
                <div style={{ display: "flex", alignItems: "flex-start" }}>
                  <Checkbox
                    checked={title.selected}
                    onChange={() => toggleTitleSelection(index)}
                    style={{ marginRight: 12, marginTop: 4 }}
                  />
                  <div style={{ flex: 1 }}>
                    <Text strong style={{ fontSize: 15 }}>
                      {title.title}
                    </Text>
                    {title.description && (
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {title.description}
                        </Text>
                      </div>
                    )}
                    <div style={{ marginTop: 8 }}>
                      <Space size={4} wrap>
                        {title.targetKeyword && (
                          <Tag color="blue" style={{ fontSize: 11 }}>
                            {title.targetKeyword}
                          </Tag>
                        )}
                        {title.searchIntent && (
                          <Tag color="purple" style={{ fontSize: 11 }}>
                            {title.searchIntent}
                          </Tag>
                        )}
                        {title.copilotlyFeature && (
                          <Tag color="green" style={{ fontSize: 11 }}>
                            {title.copilotlyFeature}
                          </Tag>
                        )}
                        {title.trendRelevance && (
                          <Tag color="orange" style={{ fontSize: 11 }}>
                            {title.trendRelevance}
                          </Tag>
                        )}
                      </Space>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* No titles yet */}
        {!titlesLoading && generatedTitles.length === 0 && (
          <div style={{ textAlign: "center", padding: 40, background: "#fafafa", borderRadius: 8 }}>
            <EditOutlined style={{ fontSize: 48, color: "#d9d9d9", marginBottom: 16 }} />
            <div>
              <Text type="secondary">No titles generated yet.</Text>
            </div>
            <Button
              type="primary"
              onClick={handleGenerateTitles}
              loading={titlesLoading}
              style={{ marginTop: 16 }}
            >
              Generate Titles
            </Button>
          </div>
        )}

        {selectedCount > 0 && (
          <Alert
            type="success"
            message={`${selectedCount} blog${selectedCount !== 1 ? "s" : ""} will be generated`}
            description={`The campaign will create content for the ${selectedCount} selected title${selectedCount !== 1 ? "s" : ""} at the rate of 1 blog every ${formData.minutesBetweenBlogs} minutes.`}
            showIcon
            style={{ marginTop: 24 }}
          />
        )}
      </div>
    );
  };

  // Render Step 5: Schedule & Review
  const renderStep5 = () => (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>
        Schedule & Review
      </Title>

      {/* Schedule Type */}
      <Form.Item label="When to Start">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Card
              hoverable
              style={{
                borderColor: formData.scheduleType === "immediate" ? "#1890ff" : "#d9d9d9",
                borderWidth: formData.scheduleType === "immediate" ? 2 : 1,
                cursor: "pointer",
              }}
              onClick={() => updateFormData("scheduleType", "immediate")}
            >
              <div style={{ textAlign: "center" }}>
                <RocketOutlined style={{ fontSize: 28, color: "#1890ff", marginBottom: 8 }} />
                <Title level={5} style={{ margin: 0 }}>
                  Start Immediately
                </Title>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Begin generation right away
                </Text>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card
              hoverable
              style={{
                borderColor: formData.scheduleType === "scheduled" ? "#722ed1" : "#d9d9d9",
                borderWidth: formData.scheduleType === "scheduled" ? 2 : 1,
                cursor: "pointer",
              }}
              onClick={() => updateFormData("scheduleType", "scheduled")}
            >
              <div style={{ textAlign: "center" }}>
                <ScheduleOutlined style={{ fontSize: 28, color: "#722ed1", marginBottom: 8 }} />
                <Title level={5} style={{ margin: 0 }}>
                  Schedule for Later
                </Title>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Set a specific start time
                </Text>
              </div>
            </Card>
          </Col>
        </Row>
      </Form.Item>

      {formData.scheduleType === "scheduled" && (
        <>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Start Date & Time" required>
                <DatePicker
                  showTime
                  format="YYYY-MM-DD HH:mm"
                  size="large"
                  style={{ width: "100%" }}
                  value={formData.scheduledFor ? moment(formData.scheduledFor) : null}
                  onChange={(date) => updateFormData("scheduledFor", date?.toISOString())}
                  disabledDate={(current) => current && current < moment().startOf("day")}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Timezone">
                <Select
                  showSearch
                  size="large"
                  value={formData.timezone}
                  onChange={(value) => updateFormData("timezone", value)}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: "100%" }}
                >
                  {moment.tz.names().map((tz) => (
                    <Option key={tz} value={tz}>
                      {formatTimezone(tz)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </>
      )}

      {/* End Date (optional) */}
      <Form.Item label="End Date (optional)">
        <DatePicker
          showTime
          format="YYYY-MM-DD HH:mm"
          size="large"
          style={{ width: "100%" }}
          value={formData.scheduledEndDate ? moment(formData.scheduledEndDate) : null}
          onChange={(date) => updateFormData("scheduledEndDate", date?.toISOString())}
          disabledDate={(current) => {
            if (formData.scheduledFor) {
              return current && current < moment(formData.scheduledFor);
            }
            return current && current < moment().startOf("day");
          }}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          Campaign will stop after this time even if not all blogs are generated
        </Text>
      </Form.Item>

      <Divider>Campaign Summary</Divider>

      {/* Summary */}
      <Card style={{ background: "#fafafa" }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Text strong>Campaign Name:</Text>
            <br />
            <Text>{formData.name || "-"}</Text>
          </Col>
          <Col xs={24} sm={12}>
            <Text strong>Campaign Type:</Text>
            <br />
            <Tag color={campaignTypeInfo[formData.campaignType]?.color}>
              {campaignTypeInfo[formData.campaignType]?.title}
            </Tag>
          </Col>
          <Col xs={24} sm={12}>
            <Text strong>Main Topic:</Text>
            <br />
            <Text>{formData.mainTopic || "-"}</Text>
          </Col>
          <Col xs={24} sm={12}>
            <Text strong>Target Keywords:</Text>
            <br />
            {formData.targetKeywords.length > 0
              ? formData.targetKeywords.map((kw) => (
                  <Tag key={kw} color="blue" style={{ marginBottom: 4 }}>
                    {kw}
                  </Tag>
                ))
              : "-"}
          </Col>
          <Col xs={24} sm={12}>
            <Text strong>Blogs to Generate:</Text>
            <br />
            <Text>{generatedTitles.filter((t) => t.selected).length}</Text>
          </Col>
          <Col xs={24} sm={12}>
            <Text strong>Word Count Range:</Text>
            <br />
            <Text>
              {formData.wordCountMin} - {formData.wordCountMax} words
            </Text>
          </Col>
          <Col xs={24} sm={12}>
            <Text strong>AI Provider:</Text>
            <br />
            <Text>
              {aiProviderOptions.find((p) => p.value === formData.aiProvider)?.label}
            </Text>
          </Col>
          <Col xs={24} sm={12}>
            <Text strong>Publishing Strategy:</Text>
            <br />
            <Tag color={formData.publishingStrategy === "backdate" ? "green" : formData.publishingStrategy === "drip" ? "blue" : "default"}>
              {publishingStrategyOptions.find((p) => p.value === formData.publishingStrategy)?.label}
            </Tag>
            {formData.publishingStrategy === "backdate" && formData.backdateStartDate && (
              <Text type="secondary" style={{ marginLeft: 8 }}>
                from {moment(formData.backdateStartDate).format("MMM D, YYYY")}
              </Text>
            )}
          </Col>
          <Col xs={24} sm={12}>
            <Text strong>Schedule:</Text>
            <br />
            <Text>
              {formData.scheduleType === "immediate"
                ? "Start immediately"
                : formData.scheduledFor
                ? moment(formData.scheduledFor).format("YYYY-MM-DD HH:mm") +
                  ` (${formData.timezone})`
                : "-"}
            </Text>
          </Col>
          <Col xs={24}>
            <Text strong>Content Sections:</Text>
            <br />
            <Space wrap>
              {formData.includesFAQ && <Tag color="green">FAQ ({formData.faqCount} Q&A)</Tag>}
              {formData.includesHowTo && <Tag color="green">How-To</Tag>}
              {formData.includesCaseStudy && <Tag color="green">Case Study</Tag>}
              {formData.includesStatistics && <Tag color="green">Statistics</Tag>}
            </Space>
          </Col>
          <Col xs={24}>
            <Text strong>SEO Features:</Text>
            <br />
            <Space wrap>
              {formData.generateSchema &&
                formData.schemaTypes.map((type) => (
                  <Tag key={type} color="purple">
                    {type}
                  </Tag>
                ))}
              {formData.optimizeForFeaturedSnippets && (
                <Tag color="purple">Featured Snippets</Tag>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Selected Titles */}
      <Card title="Selected Blog Titles" size="small" style={{ marginTop: 16 }}>
        {generatedTitles.filter((t) => t.selected).map((title, idx) => (
          <div key={idx} style={{ marginBottom: 8 }}>
            <Text strong>{idx + 1}. </Text>
            <Text>{title.title}</Text>
            {title.targetKeyword && (
              <Tag color="blue" style={{ marginLeft: 8, fontSize: 11 }}>
                {title.targetKeyword}
              </Tag>
            )}
          </div>
        ))}
      </Card>

      <Alert
        style={{ marginTop: 16 }}
        type="info"
        showIcon
        message="Ready to create campaign"
        description={`This will create a campaign to generate ${generatedTitles.filter((t) => t.selected).length} blog posts about "${formData.mainTopic}". ${
          formData.scheduleType === "immediate"
            ? "Generation will start immediately after creation."
            : `Generation will start at the scheduled time.`
        }`}
      />
    </div>
  );

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderStep1();
      case 1:
        return renderStep2();
      case 2:
        return renderStep3();
      case 3:
        return renderStep4();
      case 4:
        return renderStep5();
      default:
        return null;
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: "0 auto" }}>
      <Card>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => history.push("/super-admin/blog-campaigns")}
          >
            Back to Campaigns
          </Button>
          <Title level={3} style={{ margin: "16px 0 0 0" }}>
            Create Blog Campaign
          </Title>
          <Text type="secondary">
            Set up an automated campaign to generate SEO-optimized blog content
          </Text>
        </div>

        {/* Steps */}
        <Steps current={currentStep} style={{ marginBottom: 32 }}>
          <Step title="Topic" icon={<BulbOutlined />} />
          <Step title="Content & SEO" icon={<SearchOutlined />} />
          <Step title="Generation" icon={<TagsOutlined />} />
          <Step title="Select Titles" icon={<EditOutlined />} />
          <Step title="Review" icon={<CheckOutlined />} />
        </Steps>

        {/* Form */}
        <Form layout="vertical">{renderStepContent()}</Form>

        {/* Navigation */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 32,
            paddingTop: 16,
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <Button
            size="large"
            onClick={handlePrev}
            disabled={currentStep === 0}
            icon={<ArrowLeftOutlined />}
          >
            Previous
          </Button>

          {currentStep < 4 ? (
            <Button
              type="primary"
              size="large"
              onClick={handleNext}
              loading={currentStep === 2 && titlesLoading}
              icon={<ArrowRightOutlined />}
            >
              {currentStep === 2 ? "Generate Titles" : "Next"}
            </Button>
          ) : (
            <Button
              type="primary"
              size="large"
              onClick={handleSubmit}
              loading={loading}
              icon={<RocketOutlined />}
            >
              Create Campaign
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default BlogCampaignCreate;
